import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { FadeUp, Eyebrow } from "@/components/motion";
import Seo from "@/components/Seo";
import Marquee from "@/components/Marquee";

const TIMELINE = [
  { year: "2015", text: "Metamorphosis published — the book that began as a very personal journey and transformed her spiritually." },
  { year: "2015", text: "Featured in Focus On Women magazine." },
  { year: "2019", text: "Nano, The Continuation, published — Miranda's search for a cure becomes a fight for her life." },
  { year: "2020", text: "Third place at the SABA Book Awards in London, England." },
  { year: "2023", text: "Transhuman, The Final Chapter — the truth of the program, and what it means for all of humanity." },
];

export default function About() {
  const { data: books } = useQuery({
    queryKey: ["books"],
    queryFn: async () => (await api.get("/books")).data,
  });

  return (
    <>
      <Seo
        title="About Isabella MacLeod — Nurse. Survivor. Author."
        description="Isabella MacLeod: former registered nurse of twenty-two years, Nova Scotia author of the Miranda trilogy, SABA Book Awards winner, and advocate for Morgellons and chronic Lyme awareness."
        path="/about"
      />
      <section className="mx-auto max-w-[1440px] px-5 pb-20 pt-32 sm:px-8 lg:px-14 lg:pt-40">
        <div className="grid items-start gap-14 lg:grid-cols-12 lg:gap-20">
          <FadeUp className="lg:col-span-5">
            <div className="clip-frame relative overflow-hidden ring-1 ring-white/10">
              <img
                src="/img/coast2.jpg"
                alt="The Nova Scotian Atlantic coast"
                className="h-full w-full object-cover"
                style={{ filter: "saturate(0.7) brightness(0.85)" }}
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#070A11]/80 via-transparent to-transparent" />
              <div
                className="pointer-events-none absolute inset-0"
                style={{ boxShadow: "inset 0 0 90px rgba(0,240,255,0.1)" }}
              />
            </div>
            <p className="mt-4 text-right font-mono text-[10px] uppercase tracking-[0.25em] text-mute2">
              The Nova Scotian coast — where the trilogy was written
            </p>
          </FadeUp>

          <div className="lg:col-span-7">
            <FadeUp>
              <Eyebrow className="mb-6">About the Author</Eyebrow>
              <h1 className="font-serif text-5xl font-light leading-[1.02] tracking-tight text-ink sm:text-6xl">
                Nurse. Survivor. <span className="serif-italic text-cyanx">Witness.</span>
              </h1>
            </FadeUp>
            <FadeUp delay={0.1}>
              <div className="mt-9 space-y-5 text-base font-light leading-relaxed text-dim sm:text-lg">
                <p>
                  Isabella MacLeod is a former R.N. of twenty-two years with numerous specialties.
                  She is a Canadian author who resides in Nova Scotia, a mother of two adult
                  children — and the author of the Miranda trilogy:{" "}
                  <span className="text-ink">Metamorphosis</span> (2015),{" "}
                  <span className="text-ink">Nano</span> (2019), and{" "}
                  <span className="text-ink">Transhuman</span>, The Final Chapter.
                </p>
                <p>
                  She is a best-selling Amazon author and co-author of{" "}
                  <span className="text-ink">The Wellness Universe Guide to Complete Self-Care:
                  25 Tools for Happiness</span>, writing Chapter Two — "Positive Mantras:
                  Overcome Adversity With Powerful Self Talk," edited by Anna Pereira. In 2020
                  she won third place in the SABA Book Awards in London, England, and she was
                  published in <span className="text-ink">Focus On Women</span> magazine in 2015.
                </p>
                <p>
                  Isabella is a Morgellons sufferer who also lives with chronic Lyme disease.
                  She has participated in research with the CEHF and speaks on nanotechnology,
                  targeted individuals, and non-consensual bio-experimentation — committed to
                  bringing public awareness to these important issues.
                </p>
              </div>
            </FadeUp>

            <FadeUp delay={0.15} className="mt-12">
              <div className="flex flex-wrap gap-4">
                <a
                  href="https://substack.com/@nanowoman"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="about-substack-link"
                  className="group flex items-center gap-2 rounded-full border border-white/15 px-6 py-3.5 font-mono text-[10px] uppercase tracking-[0.22em] text-ink transition-all duration-300 hover:border-cyanx/60 hover:text-cyanx"
                >
                  Essays on Substack
                  <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </a>
                <a
                  href="https://www.booksie.com/users/isabella-macleod-264730"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="about-booksie-link"
                  className="group flex items-center gap-2 rounded-full border border-white/15 px-6 py-3.5 font-mono text-[10px] uppercase tracking-[0.22em] text-ink transition-all duration-300 hover:border-cyanx/60 hover:text-cyanx"
                >
                  Samples on Booksie
                  <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </a>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      <Marquee
        items={[
          "Metamorphosis — 2015",
          "SABA Book Awards · London 2020",
          "Nano — 2019",
          "Amazon Best-Selling Author",
          "Transhuman — 2023",
          "Focus On Women · 2015",
        ]}
      />

      <section className="mx-auto max-w-[1440px] px-5 py-24 sm:px-8 lg:px-14 lg:py-32">
        <FadeUp className="mb-16">
          <Eyebrow className="mb-5">A Life in Print</Eyebrow>
          <h2 className="font-serif text-4xl font-light tracking-tight text-ink sm:text-5xl">
            The years, <span className="serif-italic text-cyanx">line by line.</span>
          </h2>
        </FadeUp>
        <div>
          {TIMELINE.map((t, i) => (
            <FadeUp key={i} delay={i * 0.05}>
              <div className="grid grid-cols-12 gap-4 border-t border-white/[0.07] py-8">
                <div className="col-span-3 sm:col-span-2">
                  <span className="font-mono text-sm tracking-[0.15em] text-cyanx">{t.year}</span>
                </div>
                <p className="col-span-9 max-w-2xl text-base font-light leading-relaxed text-dim sm:col-span-10 sm:text-lg">
                  {t.text}
                </p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {books && books.length > 0 && (
        <section className="border-t border-white/[0.07] bg-[#0E1422]/40">
          <div className="mx-auto max-w-[1440px] px-5 py-20 sm:px-8 lg:px-14">
            <FadeUp className="mb-12 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
              <div>
                <Eyebrow className="mb-4">Her Work</Eyebrow>
                <h2 className="font-serif text-3xl font-light tracking-tight text-ink sm:text-4xl">
                  The books
                </h2>
              </div>
            </FadeUp>
            <div className="grid gap-8 sm:grid-cols-3">
              {books.map((b, i) => (
                <FadeUp key={b.slug} delay={i * 0.1}>
                  <a
                    href={`/books/${b.slug}`}
                    data-testid={`about-book-link-${b.slug}`}
                    className="group block"
                  >
                    <img
                      src={b.cover}
                      alt={`${b.title} — cover art`}
                      className="aspect-[2/3] w-full object-cover shadow-xl ring-1 ring-white/10 transition-transform duration-500 group-hover:-translate-y-2"
                    />
                    <p className="mt-4 font-serif text-xl font-light text-ink group-hover:text-cyanx">
                      {b.title} <span className="text-mute2">— {b.year}</span>
                    </p>
                  </a>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
